// noinspection JSUnusedGlobalSymbols

//region Imports
import React, {useState, useEffect} from "react";
import type {NextPage} from "next";
import {useRouter} from "next/router";
import {useSearchParams} from "next/navigation";
import dynamic from "next/dynamic";
// import Link from "next/link";
import type {Application} from '@splinetool/runtime';
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from 'lodash';

import {pages} from "@isudio/config";
import {$e, $store} from '@isudio/globals';
import {$core} from "@isudio/core";
import {useRxSync} from '@isudio/utils';
import {PageHead,  SEOContent, LoadingSpinner, VideoPlayer, useOpenComponent, useDialogController, FadeTransition} from '@isudio/ui';
// import {General as Layout, PageHead,  SEOContent, LoadingSpinner, VideoPlayer, useOpenComponent, useDialogController, FadeTransition} from '@isudio/ui';
import type {ISignupInvite, IUserSignupResponse, IUser} from "@isudio/data";

const Spline = dynamic(
  () => import('@isudio/ui').then((mod) => mod.Spline),
  { ssr: false }
);
//endregion Imports

const Home: NextPage = () => {
  //region Variables
  const router = useRouter();
  const searchParams = useSearchParams();
  const [queryParams, setQueryParams] = useState<any>(null);
  const windowState: any = _.get(useRxSync($store.App.state$), 'window');
  // const [show3DHome, setShow3DHome] = useState<boolean>(null);
  const [loading, setLoading] = useState(true);
  // const [showHeader, setShowHeader] = useState(true);
  // const [showFooter, setShowFooter] = useState(false);
  const [storeReady, setStoreReady] = useState(false);

  const pageTitle = pages.home.title;
  const pageCaption = pages.home.caption;
  const pageImage = pages.home.image;
  const pageUrl = pages.home.url;

  const seoContent = pages.home.seoContent;
  // const splineSettings = pages.home.spline.settings[show3DHome ? "home" : "text"];
  const splineSettings = pages.home.spline.settings.home;
  const splineLinks: any[] = pages.home.spline.links;
  // const splineLinkNames: string[] = splineLinks.map((o: any) => o.name);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  //endregion Variables

  //region Methods & Event Handlers
  const {isDialogOpen} = useDialogController();
  const openComponent = useOpenComponent();

  const openSignup = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {email: queryParams.signup}
      };

      await $core.verifyInvite(payload).then((data: ISignupInvite) => {
        const _data = {
          accountType: data.role,
          email: data.email,
          organization: data.organization,
          institution: data.institution
        }
        openComponent("signUp", _data);

      }).catch((e: any) => {
        const x = setTimeout(async () => {
          _.toastError(_.get(e, 'message'));
          clearTimeout(x);
        }, 10);
      });

      window.history.replaceState({}, '', window.location.pathname);
      setLoading(false);
      clearTimeout(t);
    }, 100);
  };

  const openActivate = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {
          email: queryParams.activate,
          otp: queryParams.otp
        }
      };

      await $core.verifyActivation(payload).then((user: IUserSignupResponse) => {
        $store.User.user$.next(user);
        const _data = {
          user,
          otp: queryParams.otp
        };
        openComponent("activateAccount", _data);

      }).catch((e: any) => {
        if (['otpExpired', 'otpIncorrect'].indexOf(_.get(e, 'code')) > -1) {
          const _data = {
            user: e?.data,
            otp: queryParams.otp,
            error: e
          };
          openComponent("activateAccount", _data);

        } else {
          const x = setTimeout(async () => {
            _.toastError(_.get(e, 'message'));
            clearTimeout(x);
          }, 10);
        }
      });

      window.history.replaceState({}, '', window.location.pathname);
      setLoading(false);
      clearTimeout(t);
    }, 100);
  };

  const openChangePassword = () => {
    const t = setTimeout(async () => {
      const payload = {
        params: {
          email: queryParams.changePassword,
          otp: queryParams.otp
        }
      };

      await $core.verifyChangePassword(payload).then((user: IUser) => {
        $store.User.user$.next(user);
        const _data = {
          user: user,
          otp: queryParams.otp
        };
        openComponent("changePassword", _data);

      }).catch((e: any) => {
        if (['otpExpired', 'otpIncorrect'].indexOf(_.get(e, 'code')) > -1) {
          const _data = {
            user: e?.data,
            otp: queryParams.otp,
            error: e
          };
          openComponent("changePassword", _data);

        } else {
          const x = setTimeout(async () => {
            _.toastError(_.get(e, 'message'));
            clearTimeout(x);
          }, 10);
        }
      });

      window.history.replaceState({}, '', window.location.pathname);
      setLoading(false);
      clearTimeout(t);
    }, 100);
  };

  const onSplineScroll = async () => {
    $store.App.showMenu$.next(false);
  };

  const onSplineClick = async (target: string) => {
    if (!target) return;

    const targetLink: any = _.find(splineLinks, {name: target});
    if (!_.isNothing(targetLink)) {
      switch (targetLink.type) {
        case "menu":
          $store.App.menuClicked$.next(true);
          break;

        case "video":
          setVideoUrl(targetLink.url);
          break;

        case "dialog":
          openComponent(targetLink.id)
          break;

        case "route":
          await router.push(targetLink.url, undefined, {shallow: false});
          break;

        default:
          _.toastSuccess(`${targetLink.name} clicked!`);
      }
    }
  };

  const onSplineLoad = (_app: Application) => {
    if (!queryParams) setLoading(false);
    // $store.App.show3DHome = false;
    $e.$b($e.events.loadBackgroundCache, undefined, false);
  };

  // const onShow3DHome = () => {
  //   $store.App.show3DHome = true;
  //   setShow3DHome(true);
  //   setLoading(true);
  // };

  // const onLinkClick = async (link: any) => {
  //   if (!link.url) return;
  //   await router.push(link.url, undefined, {shallow: false});
  // };

  // const onScroll = (
  //   isOverflow: boolean,
  //   _scrollTop: number,
  //   _scrollProgress: number,
  //   containerRef: React.RefObject<HTMLDivElement>
  // ) => {
  //   const el = containerRef.current;
  //   if (!el) return;
  //
  //   const distanceFromTop = el.scrollTop;
  //   const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
  //
  //   // console.log(`isOverflow: ${isOverflow},\ndistanceFromTop: ${distanceFromTop},\ndistanceFromBottom: ${distanceFromBottom}`);
  //
  //   const HEADER_SHOW_THRESHOLD = 20;
  //   const FOOTER_SHOW_THRESHOLD = 30;
  //
  //   setShowHeader(distanceFromTop <= HEADER_SHOW_THRESHOLD);
  //   setShowFooter(
  //     !isOverflow ||
  //     (isOverflow &&
  //       distanceFromTop > HEADER_SHOW_THRESHOLD ||
  //       distanceFromBottom <= FOOTER_SHOW_THRESHOLD
  //     )
  //   );
  // };

  // const getSplineHeight = () => {
  //   if (show3DHome || !windowState || !windowState?.orientation) return "0px";
  //   const orientation = windowState?.orientation;
  //   if (!splineSettings[orientation].custom || !splineSettings[orientation].defaults) return "0px";
  //
  //   const width = windowState?.width;
  //   const height = windowState?.height;
  //
  //   const custom = _.find(splineSettings[orientation].custom, {width: width, height: height});
  //   if (custom) {
  //     return `${custom.containerHeight}px`;
  //   }
  //
  //   if (_.isMobile() && width < 1024 && height < 1024) {
  //     return `${splineSettings[orientation].defaults.mobile.containerHeight}px`
  //   }
  //
  //   if (_.isTablet() && width >= 1024 || height >= 1024) {
  //     return `${splineSettings[orientation].defaults.tablet.containerHeight}px`
  //   }
  //
  //   return `${splineSettings[orientation].defaults.desktop.containerHeight}px`
  // }
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    const signup = searchParams.get('signup');
    const activate = searchParams.get('activate');
    const changePassword = searchParams.get('changePassword');
    const otp = searchParams.get('otp');
    const hasParams = signup || activate || changePassword;

    const initialize = () => {
      if (hasParams) {
        // $store.App.show3DHome = false;
        // setShow3DHome(false);
        setQueryParams({signup, activate, changePassword, otp});

      } else {
        // const currentPreference = $store.App.show3DHome;
        // setShow3DHome(currentPreference ?? false);
      }

      setStoreReady(true);
    };

    if ($store.App.storeReady) {
      initialize();
      return;
    }

    const sub = $e.events.storeReady.event.subscribe(initialize);
    return () => sub.unsubscribe();
  }, [searchParams]);

  useEffect(() => {
    if (queryParams) {
      if (queryParams.signup) {
        openSignup();
      }

      if (queryParams.activate) {
        openActivate();
      }

      if (queryParams.changePassword) {
        openChangePassword();
      }
    }
  }, [queryParams]);
  //endregion Effects

  //region HTML Elements
  const loadingSpinner = (
    // <LoadingSpinner
    //   video={show3DHome ? "/assets/media/loader/loader.webm" : undefined}
    //   noTransparency={true}
    // />
    <LoadingSpinner
      video={"/assets/media/loader/loader.webm"}
      noTransparency={true}
    />
  );

  const seoElement = (
    <SEOContent
      pageTitle={pageTitle}
      seoContent={seoContent}
    />
  );

  const splineHome = (
    <Spline
      windowState={windowState}
      sceneType="home"
      splineSettings={splineSettings}
      splineLinks={splineLinks}
      onSplineLoad={onSplineLoad}
      onSplineClick={onSplineClick}
      onSplineScroll={onSplineScroll}
    />
  );

  // const splineText = (
  //   <Spline
  //     windowState={windowState}
  //     sceneType="text"
  //     splineSettings={splineSettings}
  //     onSplineLoad={onSplineLoad}
  //     style={{
  //       width: '100%',
  //       height: getSplineHeight()
  //     }}
  //   />
  // );

  // const htmlHome = (
  //   <div className="flex flex-col items-center justify-center text-center">
  //     <div className="w-full">
  //       {splineText}
  //     </div>
  //
  //     <div className="flex flex-col gap-6 items-center justify-center text-center">
  //       <div className="flex w-fit h-fit text-xs md:text-sm gap-2 md:gap-4 items-center justify-center">
  //         {pages.home.htmlHome.links.map((link: any, index: number) => (
  //           <button
  //             key={index}
  //             className="w-fit px-2 md:px-4 py-2 flex items-center justify-center tracking-tight leading-none cursor-pointer focus:outline-none rounded-md transition duration-200 text-white bg-custom-gray-550 hover:text-generic-hover hover:scale-105 hover:bg-custom-gray-500"
  //             type="button"
  //             tabIndex={-1}
  //             onClick={() => onLinkClick(link)}
  //           >
  //             <span className="">
  //               {link.name}
  //             </span>
  //           </button>
  //         ))}
  //       </div>
  //
  //       <Link
  //         className="text-xs md:text-sm tracking-tight leading-none underline cursor-pointer transition duration-200 text-custom-gray-600 dark:text-pale-white hover:text-generic-hover hover:scale-105"
  //         href={"#"}
  //         tabIndex={-1}
  //         onClick={onShow3DHome}
  //       >
  //         {pages.home.htmlHome.home3DLink}
  //       </Link>
  //     </div>
  //   </div>
  // );

  // const layout = (
  //   <Layout
  //     hfPosition="fixed"
  //     showHeader={showHeader}
  //     showFooter={showFooter}
  //     onScroll={onScroll}
  //     enableScrollTracking
  //   >
  //     <div
  //       className="relative flex flex-1 flex-col w-full"
  //     >
  //       <div
  //         className="w-full h-full grid px-[5%] md:px-[10%] py-8 gap-3 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed"
  //       >
  //         {htmlHome}
  //       </div>
  //     </div>
  //   </Layout>
  // );

  const toastContainer = (
    <ToastContainer
      position="bottom-center"
      autoClose={3000}
      newestOnTop={true}
      hideProgressBar={true}
      closeButton={false}
      closeOnClick={true}
      pauseOnHover={true}
      draggable={false}
      theme="dark"
      transition={FadeTransition}
      className="absolute left-0 right-0 bottom-0 z-9999 pointer-events-none"
      toastClassName="mx-auto pointer-events-auto flex m-0! items-center gap-2 rounded-xl shadow-lg border-none transition-all duration-200"
    />
  );

  const videoPlayer = (
    <VideoPlayer
      url={videoUrl}
      onClose={() => setVideoUrl(null)}
    />
  );
  //endregion HTML Elements

  if (!storeReady) {
    return (
      <>
        <PageHead
          title={pageTitle}
          caption={pageCaption}
          image={pageImage}
          url={pageUrl}
        />
        {loadingSpinner}
      </>
    );
  }

  return (
    <>
      <PageHead
        title={pageTitle}
        caption={pageCaption}
        image={pageImage}
        url={pageUrl}
      />

      <div
        className="w-screen h-screen relative overflow-hidden">
        {loading && (
          loadingSpinner
        )}

        {seoElement}

        {/*{show3DHome ? splineHome : layout}*/ splineHome}

        {!isDialogOpen && (
          toastContainer
        )}

        {videoUrl && (
          videoPlayer
        )}
      </div>
    </>
  );
};

export default Home;